import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import usePDPFabricFilters from 'data-hooks/usePDPFabricFilters';
import usePDPFullScreenClickEvent from 'data-hooks/useEvents/usePDPFullScreenClickEvent';
import usePDPBasicDetails from 'components/consumer/PDP/state/context/usePDPBasicDetails';
import useUpholsteryFilteredEvent from 'data-hooks/useEvents/useUpholsteryFilteredEvent';
import usePDPPrice from 'data-hooks/usePDPPrice';
import useFabricFavs from 'data-hooks/useFabricFavs';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import PDPSelectUpholsteryUI from './PDPSelectUpholsteryUI';
import usePDPOptions from '../state/context/usePDPOptions';
import PDPSelectUpholsteryPlaceholder from './PDPSelectUpholsteryPlaceholder';

const PDPSelectUpholsteryInner = ({
  isEditOrder = false,
  mode = 'pdp',
  onMoreMaterialsClick,
  optionsLoading,
  productId = null,
  slug,
  setMarxentSelectedFabric,
}) => {
  const { data: basicDetails } = usePDPBasicDetails();
  const { data: priceData } = usePDPPrice(productId, slug);

  const {
    loading,
    error,
    data: optionData,
    actions: { onOptionValueHover, onOptionValueSelection },
  } = usePDPOptions();

  const {
    data: {
      activeColorFilters,
      activeNamedFilters,
      allMaterials,
      allMaterialsLength,
      disabledMaterialIds,
      favMaterials,
      filterCounts,
      filteredMaterials,
      flashSaleOptionIds,
      isClearanceFabricAvailable,
      isSelectedOptionsQuickship,
      optionName,
      selectedMaterial,
      selectedOptionsStockQty,
      upholsteryOptionId,
    },
    actions: { clearAllFilters, toggleNamedFilter, toggleColorFilter },
  } = usePDPFabricFilters(slug, isEditOrder);

  const {
    actions: { setOptionAsFav, removeOptionAsFav } = {},
  } = useFabricFavs({ loadMaterialDetails: true });

  useEffect(() => {
    if (selectedMaterial?.sku) {
      setMarxentSelectedFabric?.(selectedMaterial.sku.replace('-', ''));
    }
  }, [selectedMaterial?.sku, setMarxentSelectedFabric]);

  const { fullScreenClickEvent } = usePDPFullScreenClickEvent(
    basicDetails,
    optionData,
    priceData
  );

  const { upholsteryFilteredEvent } = useUpholsteryFilteredEvent(
    basicDetails,
    optionData,
    priceData,
    'PDP'
  );

  const fabricFavProps = useMemo(
    () => ({
      actions: {
        setOptionAsFav,
        removeOptionAsFav,
      },
      data: {
        favMaterials,
        favProductData: {
          name: basicDetails?.name,
          productId: basicDetails?.id,
          category: basicDetails?.type.name,
        },
        defaultToMaterial: allMaterials?.[0],
      },
    }),
    [
      allMaterials,
      basicDetails?.id,
      basicDetails?.name,
      basicDetails?.type.name,
      favMaterials,
      removeOptionAsFav,
      setOptionAsFav,
    ]
  );

  if (error) {
    return null;
  }

  return (
    <PDPSelectUpholsteryUI
      activeColorFilters={activeColorFilters}
      activeNamedFilters={activeNamedFilters}
      allMaterialsLength={allMaterialsLength}
      clearAllFilters={clearAllFilters}
      disabledMaterialIds={disabledMaterialIds}
      fabricFavProps={fabricFavProps}
      filterCounts={filterCounts}
      flashSaleOptionIds={flashSaleOptionIds}
      fullScreenClickEvent={fullScreenClickEvent}
      isClearanceFabricAvailable={isClearanceFabricAvailable}
      isEditOrder={isEditOrder}
      isSelectedOptionsQuickship={isSelectedOptionsQuickship}
      loading={loading || optionsLoading}
      materials={filteredMaterials}
      mode={mode}
      onMoreMaterialsClick={onMoreMaterialsClick}
      onOptionValueHover={onOptionValueHover}
      onOptionValueSelection={onOptionValueSelection}
      selectedMaterial={selectedMaterial}
      selectedOptionsStockQty={selectedOptionsStockQty}
      slug={slug}
      toggleColorFilter={toggleColorFilter}
      toggleNamedFilter={toggleNamedFilter}
      upholsteryFilteredEvent={upholsteryFilteredEvent}
      upholsteryOptionId={upholsteryOptionId}
      upholsteryOptionName={optionName}
    />
  );
};

PDPSelectUpholsteryInner.propTypes = {
  isEditOrder: PropTypes.bool,
  mode: PropTypes.oneOf(['pdp', 'quickview']),
  onMoreMaterialsClick: PropTypes.func,
  optionsLoading: PropTypes.bool,
  productId: PropTypes.number,
  setMarxentSelectedFabric: PropTypes.func,
  slug: PropTypes.string,
};

const PDPSelectUpholstery = props => (
  <SuspenseWithLoader fallback={<PDPSelectUpholsteryPlaceholder />}>
    <PDPSelectUpholsteryInner {...props} />
  </SuspenseWithLoader>
);

export default PDPSelectUpholstery;
